import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { User } from '@services/AssessmentService';
import './AddReporteeModal.css';

interface AddReporteeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReporteeRelationshipData) => Promise<void>;
  users: User[];
  loading: boolean;
}

export interface ReporteeRelationshipData {
  user_id: number;
  reportees: ReporteeData[];
}

export interface ReporteeData {
  // If reportee exists in system
  reportee_id?: number;
  // If reportee doesn't exist in system
  reportee_name?: string;
  reportee_email?: string;
}

const AddReporteeModal: React.FC<AddReporteeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  users,
  loading
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedReportees, setSelectedReportees] = useState<User[]>([]);
  const [manualReportees, setManualReportees] = useState<{ name: string; email: string }[]>([]);
  const [reporteeType, setReporteeType] = useState<'existing' | 'manual'>('existing');
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualEmailError, setManualEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setSelectedUser(null);
      setSelectedUserId('');
      setSelectedReportees([]);
      setManualReportees([]);
      setReporteeType('existing');
      setManualName('');
      setManualEmail('');
      setManualEmailError('');
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleManualEmailChange = (email: string) => {
    setManualEmail(email);
    if (email && !validateEmail(email)) {
      setManualEmailError('Please enter a valid email address');
    } else {
      setManualEmailError('');
    }
  };

  const handleAddManualReportee = () => {
    if (!manualName.trim() || !manualEmail.trim()) {
      alert('Please enter both name and email');
      return;
    }

    if (!validateEmail(manualEmail)) {
      setManualEmailError('Please enter a valid email address');
      return;
    }

    // Check if email already added
    const emailExists = manualReportees.some(r => r.email.toLowerCase() === manualEmail.toLowerCase());
    if (emailExists) {
      alert('This email has already been added');
      return;
    }

    setManualReportees([...manualReportees, { name: manualName.trim(), email: manualEmail.trim() }]);
    setManualName('');
    setManualEmail('');
    setManualEmailError('');
  };

  const handleRemoveManualReportee = (index: number) => {
    setManualReportees(manualReportees.filter((_, i) => i !== index));
  };

  const getAvailableReportees = () => {
    if (!selectedUserId) return [];
    return users.filter(u => u.userId !== selectedUserId);
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      alert('Please select an employee');
      return;
    }

    const reportees: ReporteeData[] = [];

    // Add existing reportees
    selectedReportees.forEach(reportee => {
      reportees.push({ reportee_id: reportee.userId });
    });

    // Add manual reportees
    manualReportees.forEach(reportee => {
      reportees.push({
        reportee_name: reportee.name,
        reportee_email: reportee.email
      });
    });

    if (reportees.length === 0) {
      alert('Please select at least one reportee or add a manual reportee');
      return;
    }

    const relationshipData: ReporteeRelationshipData = {
      user_id: selectedUserId as number,
      reportees
    };

    setSubmitting(true);
    try {
      await onSubmit(relationshipData);
      onClose();
    } catch (error) {
      console.error('Error submitting reportee relationship:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-reportee-modal-overlay" onClick={onClose}>
      <div className="add-reportee-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-reportee-modal-header">
          <h3>Add Reportee Relationship</h3>
          <button className="add-reportee-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="add-reportee-modal-body">
          {/* Select Employee */}
          <div className="add-reportee-form-group">
            <label htmlFor="selectUser">Select Employee *</label>
            <Autocomplete
              id="selectUser"
              options={users}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
                setSelectedUserId(newValue ? newValue.userId : '');
                setSelectedReportees([]);
              }}
              disabled={loading || submitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select employee..."
                  variant="outlined"
                />
              )}
              isOptionEqualToValue={(option, value) => option.userId === value.userId}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '4px',
                }
              }}
            />
          </div>

          {selectedUserId && (
            <>
              {/* Reportee Type Selection */}
              <div className="add-reportee-form-group">
                <label>Reportee Type</label>
                <div className="add-reportee-reportee-type-selector">
                  <button
                    type="button"
                    className={`add-reportee-type-btn ${reporteeType === 'existing' ? 'active' : ''}`}
                    onClick={() => setReporteeType('existing')}
                  >
                    Select Existing Users
                  </button>
                  <button
                    type="button"
                    className={`add-reportee-type-btn ${reporteeType === 'manual' ? 'active' : ''}`}
                    onClick={() => setReporteeType('manual')}
                  >
                    Add Manual Entry
                  </button>
                </div>
              </div>

              {/* Existing Reportees Selection */}
              {reporteeType === 'existing' && (
                <div className="add-reportee-form-group">
                  <label>Select Reportees from Users</label>
                  <Autocomplete
                    multiple
                    options={getAvailableReportees()}
                    getOptionLabel={(option) => `${option.name} (${option.email})`}
                    value={selectedReportees}
                    onChange={(event, newValue) => {
                      setSelectedReportees(newValue);
                    }}
                    disabled={loading || submitting}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select reportees..."
                        variant="outlined"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          sx={{
                            backgroundColor: '#667eea',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'rgba(255, 255, 255, 0.7)',
                              '&:hover': {
                                color: 'white',
                              }
                            }
                          }}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '4px',
                      }
                    }}
                  />
                  {selectedReportees.length > 0 && (
                    <div className="add-reportee-count-badge">
                      {selectedReportees.length} reportee(s) selected
                    </div>
                  )}
                </div>
              )}

              {/* Manual Reportee Entry */}
              {reporteeType === 'manual' && (
                <div className="add-reportee-manual-section">
                  <div className="add-reportee-manual-inputs">
                    <div className="add-reportee-form-group">
                      <label htmlFor="reporteeName">Reportee Name *</label>
                      <input
                        id="reporteeName"
                        type="text"
                        className="add-reportee-form-control"
                        placeholder="Enter reportee name"
                        value={manualName}
                        onChange={(e) => setManualName(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div className="add-reportee-form-group">
                      <label htmlFor="reporteeEmail">Reportee Email *</label>
                      <input
                        id="reporteeEmail"
                        type="email"
                        className={`add-reportee-form-control ${manualEmailError ? 'error' : ''}`}
                        placeholder="reportee@example.com"
                        value={manualEmail}
                        onChange={(e) => handleManualEmailChange(e.target.value)}
                        disabled={submitting}
                      />
                      {manualEmailError && (
                        <span className="add-reportee-error-text">{manualEmailError}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="add-reportee-btn-add-manual"
                    onClick={handleAddManualReportee}
                    disabled={!manualName.trim() || !manualEmail.trim() || !!manualEmailError || submitting}
                  >
                    + Add Reportee
                  </button>

                  {/* List of manual reportees */}
                  {manualReportees.length > 0 && (
                    <div className="add-reportee-manual-list">
                      <h4>Added Reportees ({manualReportees.length})</h4>
                      {manualReportees.map((reportee, index) => (
                        <div key={index} className="add-reportee-manual-item">
                          <div className="add-reportee-manual-info">
                            <span className="add-reportee-manual-name">{reportee.name}</span>
                            <span className="add-reportee-manual-email">{reportee.email}</span>
                          </div>
                          <button
                            type="button"
                            className="add-reportee-btn-remove"
                            onClick={() => handleRemoveManualReportee(index)}
                            disabled={submitting}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Total Count */}
              {(selectedReportees.length > 0 || manualReportees.length > 0) && (
                <div className="add-reportee-total-count">
                  Total reportees to be added: <strong>{selectedReportees.length + manualReportees.length}</strong>
                </div>
              )}
            </>
          )}
        </div>

        <div className="add-reportee-modal-footer">
          <button
            className="add-reportee-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="add-reportee-btn-primary"
            onClick={handleSubmit}
            disabled={
              loading ||
              submitting ||
              !selectedUserId ||
              (selectedReportees.length === 0 && manualReportees.length === 0)
            }
          >
            {submitting ? 'Adding...' : 'Add Relationship(s)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddReporteeModal;
